import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";

const post = (ep, fields = {}) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k,v]) => { if (v != null) fd.append(k, v); });
  return axios.post(`${API_BASE}${ep}`, fd).then(r => r.data).catch(() => null);
};


export default function JobAIMatchingTab({ jobSlug, userSlug, onPropose }) {
  const FILTERS = [
    { name:"Education",       type:"edu", value:0 },
    { name:"Job Titles",      type:"jt",  value:0 },
    { name:"Skills",          type:"sk",  value:0 },
    { name:"Industries",      type:"in",  value:0 },
    { name:"Management Level",type:"ml",  value:0 },
    { name:"Certifications",  type:"crt", value:0 },
    { name:"Languages",       type:"lng", value:0 },
  ];

  const [matches,    setMatches]    = useState([]);
  const [filters,    setFilters]    = useState(FILTERS);
  const [loading,    setLoading]    = useState(true);
  const [expanded,   setExpanded]   = useState(null);
  const [proposing,  setProposing]  = useState({});

  const fetchMatches = useCallback((fArr = filters) => {
    setLoading(true);
    const fd = new FormData();
    fd.append("u_slug", userSlug); fd.append("j_slug", jobSlug);
    fArr.forEach(f => {
      const key = f.type === "jt" ? "j_score" : f.type === "edu" ? "e_score" : f.type === "sk" ? "s_score"
        : f.type === "in" ? "i_score" : f.type === "lng" ? "l_score" : f.type === "ml" ? "m_score"
        : f.type === "crt" ? "c_score" : "ex_score";
      fd.append(key, f.value);
    });
    axios.post(`${API_BASE}ai-matching-v2`, fd).then(r => {
      const data = r.data;
      if (data?.matches) {
        setMatches(data.matches);
        if (data.filters_has_data) {
          setFilters(fArr.map(f => {
            const vKey = f.name.replace(/\s/g,"");
            const v = data.filters_has_data[vKey];
            return { ...f, value: v ? Math.round(v * 100) : f.value, remove: !data.filters_has_data[`${vKey}HasData`] && !v };
          }));
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [jobSlug, userSlug]);

  useEffect(() => { if (jobSlug) fetchMatches(); }, [jobSlug]);

  const handleSlider = (type, val) => {
    const updated = filters.map(f => f.type === type ? {...f, value: parseInt(val)} : f);
    setFilters(updated);
    fetchMatches(updated);
  };

  const proposeCandidate = async (candidate) => {
    setProposing(p => ({...p, [candidate.user_slug]: true}));
    const data = await post("get-available-propose-job", { user_slug: userSlug, resource_slug: candidate.user_slug });
    if (data?.status === 1) {
      await post("add-propose-job", { user_slug: userSlug, resource_slug: candidate.user_slug, job_slug: jobSlug });
      onPropose?.();
    }
    setProposing(p => ({...p, [candidate.user_slug]: false}));
  };

  const activeFilters = filters.filter(f => !f.remove);

  return (
    <div>
      {/* Score sliders */}
      {activeFilters.length > 0 && (
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, padding:"16px 18px", marginBottom:12 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"var(--fc-text-secondary)", marginBottom:12 }}>Adjust Match Weights</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:12 }}>
            {activeFilters.map(f => (
              <div key={f.type}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"var(--fc-text-muted)", fontWeight:600, marginBottom:4 }}>
                  <span>{f.name}</span><span style={{color:"#2563EB"}}>{f.value}%</span>
                </div>
                <input type="range" min="0" max="100" value={f.value}
                  onChange={e => handleSlider(f.type, e.target.value)}
                  style={{ width:"100%", accentColor:"#2563EB" }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign:"center", padding:"32px 0", color:"var(--fc-text-subtle)", fontSize:13 }}>
          <i className="fa-solid fa-spinner fa-spin" style={{marginRight:8}} />Finding matches...
        </div>
      ) : matches.length === 0 ? (
        <div style={{ padding:"32px 16px", background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, textAlign:"center", fontSize:13, color:"var(--fc-text-muted)" }}>No matching candidates found.</div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {matches.map((m, i) => {
            const isOpen = expanded === i;
            const avail  = parseInt(m.availability || 0);
            const availColor = avail >= 30 ? "var(--fc-green)" : avail >= 10 ? "var(--fc-amber)" : "var(--fc-red)";
            return (
              <div key={m.user_slug || i}
                style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, overflow:"hidden", transition:"all 0.15s" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", cursor:"pointer" }}
                  onClick={() => setExpanded(isOpen ? null : i)}>
                  {/* Rank */}
                  <div style={{ width:28, height:28, borderRadius:7, background:i===0?"var(--fc-text-primary)":i===1?"var(--fc-text-secondary)":"var(--fc-text-muted)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:"#fff", flexShrink:0 }}>
                    {i+1}
                  </div>
                  {/* Avatar */}
                  <div style={{ width:36, height:36, borderRadius:9, background:"var(--fc-primary-light)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:"#2563EB", flexShrink:0 }}>
                    {(m.resource_name||"?").charAt(0).toUpperCase()}
                  </div>
                  {/* Info */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.resource_name}</div>
                    <div style={{ fontSize:11, color:"#2563EB", fontWeight:600 }}>{m.user_profile_role}</div>
                  </div>
                  {/* Score */}
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontSize:18, fontWeight:800, color:"var(--fc-text-primary)" }}>{m.match_score}%</div>
                    <div style={{ fontSize:10, color:"var(--fc-text-subtle)" }}>match</div>
                  </div>
                  {/* Availability */}
                  <div style={{ width:7, height:7, borderRadius:"50%", background:availColor, flexShrink:0 }} />
                  {/* Expand */}
                  <i className={`fa-solid fa-chevron-${isOpen?"up":"down"}`} style={{fontSize:10,color:"var(--fc-text-subtle)",flexShrink:0}} />
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div style={{ padding:"0 16px 16px", borderTop:"1px solid var(--fc-border-light)" }}>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:10, marginTop:12 }}>
                      {m.location && <span style={{fontSize:11,color:"var(--fc-text-muted)"}}><i className="fa-solid fa-location-dot" style={{marginRight:4}} />{m.location}</span>}
                      <span style={{fontSize:11,color:availColor,fontWeight:600}}>{avail} hrs/wk available</span>
                    </div>
                    {/* Skills */}
                    {m.skill_name && (
                      <div style={{ marginBottom:10 }}>
                        <div style={{ fontSize:11, color:"var(--fc-text-subtle)", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>Skills</div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                          {m.skill_name.split(",").slice(0,8).map((s,si) => (
                            <span key={si} style={{ padding:"2px 8px", background:"var(--fc-green-light)", color:"var(--fc-green-text)", borderRadius:99, fontSize:11, fontWeight:500 }}>{s.trim()}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Score breakdown */}
                    {m.score_breakdown && (
                      <div style={{ marginBottom:12 }}>
                        <div style={{ fontSize:11, color:"var(--fc-text-subtle)", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>Score Breakdown</div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))", gap:6 }}>
                          {Object.entries(m.score_breakdown).map(([k,v]) => (
                            <div key={k} style={{ background:"var(--fc-bg-light)", borderRadius:8, padding:"6px 10px" }}>
                              <div style={{ fontSize:10, color:"var(--fc-text-subtle)", marginBottom:2 }}>{k}</div>
                              <div style={{ fontSize:13, fontWeight:700, color:"var(--fc-text-primary)" }}>{v}%</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ display:"flex", gap:8 }}>
                      <Link to={`/resources/details/${m.user_slug}`} target="_blank"
                        style={{ padding:"6px 14px", borderRadius:8, border:"none", background:"var(--fc-primary-light)", color:"#2563EB", fontSize:11, fontWeight:700, textDecoration:"none", cursor:"pointer" }}>
                        View Profile
                      </Link>
                      <button onClick={() => proposeCandidate(m)} disabled={proposing[m.user_slug]}
                        style={{ padding:"6px 14px", borderRadius:8, border:"none", background:"var(--fc-text-primary)", color:"#fff", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                        {proposing[m.user_slug] ? "Proposing..." : "Propose Candidate"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────